import { cn } from "@/lib/utils";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SlideUp, StaggerContainer, StaggerItem } from "@/components/ui/motion-wrappers";

interface SystemLadderProps {
    activeTier?: "foundation" | "growth" | "scale" | "enterprise" | "visibility" | null;
    mode?: "interactive" | "info" | "navigation";
    onSelect?: (tier: string) => void;
    className?: string;
}

export const SystemLadder = ({ activeTier, mode = "info", onSelect, className }: SystemLadderProps) => {
    const tiers = [
        {
            id: "launch",
            name: "1. Website Core",
            label: "Get Online",
            link: "/website-launch-program/",
            color: "bg-blue-500",
            description: "Professional Home Base",
        },
        {
            id: "foundation",
            name: "2. Managed CRM",
            label: "Automation",
            link: "/solutions/crm-automation/",
            color: "bg-teal-500",
            description: "Stop the Lead Leaks",
        },
        {
            id: "growth",
            name: "3. Growth Engine",
            label: "Lead Gen",
            link: "/solutions/lead-generation-system/",
            color: "bg-indigo-500",
            description: "Predictable Flow",
        },
        {
            id: "scale",
            name: "4. Scale System",
            label: "Paid Ads",
            link: "/solutions/ad-management-scale/",
            color: "bg-amber-500",
            description: "Market Dominance",
        },
    ];

    const isVisibilityActive = activeTier === "visibility";

    return (
        <div className={cn("w-full py-12 bg-muted/20 border-y", className)}>
            <div className="container mx-auto px-6">
                <SlideUp>
                    <div className="text-center mb-8">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">The Frayze Growth Ladder</span>
                        <h3 className="text-2xl font-bold mt-2">Where Are You Now?</h3>
                    </div>
                </SlideUp>

                <div className="relative mb-12">
                    {/* Connector Line (Desktop) */}
                    <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-teal-500/20 via-amber-500/20 to-slate-800/20 -translate-y-1/2 z-0" />

                    <StaggerContainer className="grid grid-cols-1 md:grid-cols-4 gap-6 relative z-10">
                        {tiers.map((tier, index) => {
                            const isActive = tier.id === activeTier;
                            // Calculate active index only if activeTier is one of the main tiers
                            const activeIndex = tiers.findIndex((t) => t.id === activeTier);
                            const isPast = activeIndex !== -1 && index < activeIndex;

                            const handleClick = (e: React.MouseEvent) => {
                                if (mode === "info") {
                                    e.preventDefault();
                                    return;
                                }
                                if (mode === "navigation" && onSelect) {
                                    e.preventDefault();
                                    onSelect(tier.id);
                                }
                            };

                            const interactiveClasses = mode === "info"
                                ? "cursor-default pointer-events-none"
                                : "cursor-pointer hover:bg-background hover:border-slate-200 hover:shadow-md grayscale hover:grayscale-0";

                            const activeClasses = isActive
                                ? "bg-background border-primary shadow-lg scale-105 z-20 grayscale-0"
                                : "bg-background/50 border-transparent";

                            return (
                                <StaggerItem key={tier.id}>
                                <Link
                                    to={mode === "navigation" ? "#" : tier.link}
                                    onClick={handleClick}
                                    className={cn(
                                        "group relative flex flex-col items-center p-6 rounded-xl border transition-all duration-300",
                                        activeClasses,
                                        !isActive && interactiveClasses
                                    )}
                                >
                                    {/* Status Indicator */}
                                    <div
                                        className={cn(
                                            "w-8 h-8 rounded-full flex items-center justify-center mb-4 transition-colors",
                                            isActive ? tier.color : isPast ? "bg-slate-200 text-slate-500" : "bg-slate-100 text-slate-300",
                                            isActive && "text-white"
                                        )}
                                    >
                                        {isActive ? (
                                            <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                                        ) : isPast ? (
                                            <Check className="w-5 h-5" />
                                        ) : (
                                            <span className="text-sm font-bold">{index + 1}</span>
                                        )}
                                    </div>

                                    <h4 className={cn("font-bold text-lg mb-1", isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground")}>
                                        {tier.name}
                                    </h4>
                                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
                                        {tier.label}
                                    </span>

                                    <p className="text-xs text-center text-muted-foreground px-2">
                                        {tier.description}
                                    </p>

                                    {/* Active Marker label */}
                                    {isActive && mode === "info" && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap">
                                            You Are Here
                                        </div>
                                    )}
                                </Link>
                                </StaggerItem>
                            );
                        })}
                    </StaggerContainer>
                </div>

                {/* Visibility Layer */}
                <SlideUp delay={0.2}>
                <div className="flex justify-center">
                    <Link
                        to="/solutions/websites/"
                        className={cn(
                            "group flex items-center gap-3 px-6 py-3 rounded-full border transition-all duration-300",
                            isVisibilityActive
                                ? "bg-blue-50 border-blue-500 shadow-md ring-2 ring-blue-200 dark:bg-blue-900/20 dark:border-blue-500"
                                : "bg-blue-50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-900 hover:border-blue-300"
                        )}
                    >
                        <div className={cn(
                            "p-1.5 rounded-full transition-colors",
                            isVisibilityActive ? "bg-blue-500 text-white" : "bg-blue-100 text-blue-600 dark:bg-blue-800 dark:text-blue-300"
                        )}>
                            <Check className="w-3 h-3" />
                        </div>
                        <div className="text-sm">
                            <span className={cn("font-bold block", isVisibilityActive ? "text-blue-700 dark:text-blue-300" : "text-blue-900 dark:text-blue-300")}>The Visibility Layer</span>
                            <span className="text-blue-700 dark:text-blue-400 text-xs">Supports every stage of the ladder &rarr;</span>
                        </div>
                        <div className={cn(
                            "ml-2 px-3 py-1 text-xs font-bold rounded-md shadow-sm transition-colors",
                            isVisibilityActive ? "bg-blue-600 text-white" : "bg-white dark:bg-slate-950 text-foreground group-hover:text-blue-600"
                        )}>
                            {isVisibilityActive ? "You Are Here" : "View Layer"}
                        </div>
                    </Link>
                </div>
                </SlideUp>
            </div>
        </div>
    );
};
